import { useState, useEffect, useCallback } from 'react'
import { operatorApi, type OperatorUser, ApiError } from '@/services/api'

interface OperatorAuthState {
  operator: OperatorUser | null
  isLoading: boolean
  isAuthenticated: boolean
  error: string | null
  session: { created_at: string; expires_at: string } | null
}

interface UseOperatorAuthReturn extends OperatorAuthState {
  login: (username: string, password: string) => Promise<boolean>
  logout: () => Promise<void>
  refreshAuth: () => Promise<void>
  isSupervisorOrAdmin: boolean
}

/**
 * Hook per gestire l'autenticazione operatore
 */
export function useOperatorAuth(): UseOperatorAuthReturn {
  const [state, setState] = useState<OperatorAuthState>({
    operator: null,
    isLoading: true,
    isAuthenticated: false,
    error: null,
    session: null
  })

  /**
   * Verifica se l'operatore è autenticato
   */
  const checkAuth = useCallback(async () => {
    setState(prev => ({ ...prev, isLoading: true, error: null }))
    
    try {
      const response = await operatorApi.getMe()
      
      if (response) {
        setState({
          operator: response.user,
          isAuthenticated: true,
          isLoading: false,
          error: null,
          session: response.session
        })
      } else {
        setState({
          operator: null,
          isAuthenticated: false,
          isLoading: false,
          error: null,
          session: null
        })
      }
    } catch (err) {
      const silent =
        err instanceof ApiError &&
        (err.statusCode === 401 || err.statusCode === 403)
      setState({
        operator: null,
        isAuthenticated: false,
        isLoading: false,
        error: silent ? null : err instanceof Error ? err.message : 'Errore verifica autenticazione',
        session: null
      })
    }
  }, [])

  /**
   * Login operatore
   */
  const login = useCallback(async (username: string, password: string): Promise<boolean> => {
    setState(prev => ({ ...prev, isLoading: true, error: null }))
    
    try {
      await operatorApi.login(username, password)

      const me = await operatorApi.getMe()
      if (!me) {
        setState({
          operator: null,
          isAuthenticated: false,
          isLoading: false,
          error:
            'Accesso non completato: il browser non ha salvato la sessione (cookie). Prova in navigazione privata o verifica che i cookie non siano bloccati per questo sito.',
          session: null
        })
        return false
      }

      setState({
        operator: me.user,
        isAuthenticated: true,
        isLoading: false,
        error: null,
        session: me.session
      })

      return true
    } catch (err) {
      const errorMessage = err instanceof ApiError 
        ? err.message 
        : err instanceof Error 
          ? err.message 
          : 'Errore durante il login'
      
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: errorMessage
      }))
      
      return false
    }
  }, [])

  /**
   * Logout operatore
   */
  const logout = useCallback(async () => {
    try {
      await operatorApi.logout()
    } catch {
      // Ignora errori logout
    }
    
    setState({
      operator: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
      session: null
    })
  }, [])

  /**
   * Ricarica stato autenticazione
   */
  const refreshAuth = useCallback(async () => {
    await checkAuth()
  }, [checkAuth])

  // Verifica autenticazione al mount
  useEffect(() => {
    checkAuth()
  }, [checkAuth])

  // Calcola se è supervisor o admin
  const isSupervisorOrAdmin = state.operator?.role === 'supervisor' || state.operator?.role === 'admin'

  return {
    ...state,
    login,
    logout,
    refreshAuth,
    isSupervisorOrAdmin
  }
}
